import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { CapaQuotationPartnerTechnologyModel } from '@generated/graphql';
import { formatDate } from '@utils/formatter';
import { getFileUrl } from '@utils/urls';
import { CustomizedTypography } from '@components/Customized';
import CertificateDetailView from './DetailView/CertificateDetailView';

interface CompanyDocumentEntryProps {
  certificate: CapaQuotationPartnerTechnologyModel;
}

const CompanyDocumentEntry: React.FunctionComponent<CompanyDocumentEntryProps> = ({
  certificate
}) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { documentEntryContainer, documentEntryData } = useStyles();
  const {
    file,
    technicalName,
    expirationPeriodStart,
    expirationPeriodEnd,
    certificationAuthority
  } = certificate;

  return (
    <>
      <CertificateDetailView
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        certificate={certificate}
      />
      <div
        className={documentEntryContainer}
        onClick={() => {
          setOpenDialog(true);
        }}
      >
        <div className={documentEntryData}>
          <div className="certificate-image">
            <img
              loading="lazy"
              src={getFileUrl(file?.fileId ?? '')}
              alt={file?.fileName ?? ''}
            />
          </div>
          <div>
            <CustomizedTypography
              className="technicalName ellipsis"
              variant="body1"
              fontWeight="500"
              color="textPrimary"
              gutterBottoms={4}
            >
              {technicalName}
            </CustomizedTypography>
            <CustomizedTypography
              variant="subtitle2"
              color="textSecondary"
              className="ellipsis"
              gutterBottoms={2}
            >
              {certificationAuthority}
            </CustomizedTypography>
            {expirationPeriodStart && expirationPeriodEnd && (
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                {formatDate(expirationPeriodStart, 'YYYY.MM.DD')}~
                {formatDate(expirationPeriodEnd, 'YYYY.MM.DD')}
              </CustomizedTypography>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyDocumentEntry;

const useStyles = makeStyles((theme) => ({
  documentEntryContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    height: '80px',
    gap: '16px',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  documentEntryData: {
    width: '100%',
    display: 'flex',
    gap: '16px',
    borderRadius: '12px',
    overflow: 'hidden',
    '&:hover': {
      '& .technicalName': {
        textDecoration: 'underline'
      }
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    '& .certificate-image': {
      minWidth: '64px',
      maxWidth: '64px',
      height: '80px',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    '& .ellipsis': {
      wordBreak: 'break-all',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 1,
      '-webkit-box-orient': 'vertical'
    }
  }
}));
